import { FunctionComponent, useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Standing, Stage } from '@/model';
import { StandingEditableFields, StandingDraft } from '../../types';
import { StandingsRow } from './StandingsRow';
import { AddStandingRow, EligibleTeam } from './AddStandingRow';
import { getQualifierZone } from '../../utils/getQualifierZone';

const PREFIX = 'pages.application_championship_stage_standings.table';

type StandingsTableProps = {
  standings: Standing[];
  stage?: Stage;
  editingUuid: string | null;
  savingUuid: string | null;
  deletingUuid: string | null;
  isAdding: boolean;
  isAddSaving: boolean;
  eligibleTeams: EligibleTeam[];
  onEnterEdit: (uuid: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (uuid: string, changes: StandingEditableFields) => void;
  onDelete: (uuid: string) => void;
  onSaveAdd: (draft: StandingDraft) => void;
  onCancelAdd: () => void;
  /** Chamado quando o DnD termina; recebe a nova ordem completa de UUIDs. */
  onReorder: (orderedUuids: string[]) => void;
  isReorderInFlight?: boolean;
};

const HEADER_KEYS = [
  'col_pos',
  'col_team',
  'col_points',
  'col_played',
  'col_wins',
  'col_draws',
  'col_losses',
  'col_goals_for',
  'col_goals_against',
  'col_goal_diff',
  'col_win_rate',
  'col_actions',
] as const;

export const StandingsTable: FunctionComponent<StandingsTableProps> = ({
  standings,
  stage,
  editingUuid,
  savingUuid,
  deletingUuid,
  isAdding,
  isAddSaving,
  eligibleTeams,
  onEnterEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onSaveAdd,
  onCancelAdd,
  onReorder,
  isReorderInFlight = false,
}) => {
  const { t } = useTranslation();

  const ids = useMemo(() => standings.map((s) => s.uuid), [standings]);
  const reorderLocked = isAdding || editingUuid !== null || isReorderInFlight;

  const handleReorder = (nextIds: string[]) => {
    if (reorderLocked) return;
    // Sanity check — sem mudanças, ignora
    let same = nextIds.length === ids.length;
    if (same) {
      for (let i = 0; i < nextIds.length; i += 1) {
        if (nextIds[i] !== ids[i]) {
          same = false;
          break;
        }
      }
    }
    if (same) return;
    onReorder(nextIds);
  };

  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="md" overflowX="auto" bg="white">
      <Text fontSize="xs" color="gray.500" px={4} pt={3} pb={2}>
        {t(`${PREFIX}.editing_hint`)}
      </Text>

      <Table size="sm" variant="simple">
        <Thead
          sx={{
            backgroundColor: 'var(--chakra-colors-gray-100) !important',
          }}
        >
          <Tr>
            <Th
              px={2}
              w="32px"
              sx={{ backgroundColor: 'var(--chakra-colors-gray-100) !important' }}
            />
            {HEADER_KEYS.map((key) => (
              <Th
                key={key}
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="wider"
                textTransform="uppercase"
                textAlign={key === 'col_team' ? 'left' : 'center'}
                px={2}
                py={3}
                borderBottom="1px solid"
                borderColor="gray.200"
                sx={{
                  // CSS global do Bootstrap (.table thead th) força cor branca
                  // e bg transparente — precisamos sobrescrever com !important.
                  color: 'var(--chakra-colors-gray-800) !important',
                  backgroundColor: 'var(--chakra-colors-gray-100) !important',
                }}
              >
                {t(`${PREFIX}.${key}`)}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Reorder.Group
          as="tbody"
          axis="y"
          values={ids}
          onReorder={handleReorder}
        >
          {standings.map((standing) => {
            const mode = savingUuid === standing.uuid
              ? 'saving'
              : editingUuid === standing.uuid
              ? 'edit'
              : 'view';
            const zone = getQualifierZone(standing.position, standings.length, stage);
            return (
              <StandingsRow
                key={standing.uuid}
                standing={standing}
                mode={mode}
                isDeleting={deletingUuid === standing.uuid}
                isDragDisabled={reorderLocked}
                zone={zone}
                onEnterEdit={() => onEnterEdit(standing.uuid)}
                onCancel={onCancelEdit}
                onSave={(changes) => onSaveEdit(standing.uuid, changes)}
                onDelete={() => onDelete(standing.uuid)}
              />
            );
          })}
        </Reorder.Group>

        {isAdding ? (
          <Tbody>
            <AddStandingRow
              eligibleTeams={eligibleTeams}
              isSaving={isAddSaving}
              onSave={onSaveAdd}
              onCancel={onCancelAdd}
              nextPosition={standings.length + 1}
            />
          </Tbody>
        ) : null}
      </Table>
    </Box>
  );
};
